import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';

import LoadingSpinner from './components/LoadingSpinner';

import { SERVICE_NAME } from './constants/AppConstants';
import {
  ACTIVATE_ACCOUNT,
  DECLARATION_URL,
  LANDING_URL,
  SELECT_SERVICE_URL,
  MESSAGE_URL,
  REGISTER_DETAILS_URL,
  REGISTER_PASSWORD_URL,
  REPORTED_VOYAGES_DASHBOARD_URL,
  REQUEST_PASSWORD_RESET_CONFIRMATION_URL,
  REQUEST_PASSWORD_RESET_URL,
  RESEND_INVITATION_CHECK_YOUR_EMAIL_URL,
  RESEND_INVITATION_URL,
  RESET_YOUR_PASSWORD,
  SIGN_IN_URL,
  INVITE_USER_CHECK_ANSWERS_URL,
  INVITE_USER_CONFIRMATION_URL,
  INVITE_USER_URL,
  MANAGE_USERS_URL,
  SPCR_DASHBOARD_URL,
  SPCR_REVIEW_REPORT_URL,
  ACCESSIBILITY_URL,
  HELP_URL,
  PRIVACY_URL,
  COOKIES_URL
} from './constants/AppUrlConstants';

import Auth from './utils/Auth';
import AdminRoutes from './utils/URL/AdminRoutes';
import ProtectedRoutes from './utils/URL/ProtectedRoutes';
import ScrollToTopOnNewPage from './utils/ScrollToTopOnNewPage';

// GENERIC
const GenericMessage = lazy(() => import('./pages/Message/GenericMessage'));
const Landing = lazy(() => import('./pages/Landing/Landing'));

const SelectService = lazy(() => import('./pages/SelectService/SelectService'));
const SpcrDashboard = lazy(() => import('./pages/Spcr/SpcrDashboard'));
const ReviewReport = lazy(() => import('./pages/Spcr/ReviewReport'));

// INVITE/REGISTER
const InviteCheckYourAnswers = lazy(() => import('./pages/Team/InviteCheckYourAnswers'));
const InviteConfirmation = lazy(() => import('./pages/Team/InviteConfirmation'));
const InviteUser = lazy(() => import('./pages/Team/InviteUser'));
const RegisterYourDetails = lazy(() => import('./pages/Registration/RegisterDetails'));
const RegisterYourPassword = lazy(() => import('./pages/Registration/RegisterPassword'));
const ResendInvitation = lazy(() => import('./pages/Registration/ResendInvitation'));
const ResendInvitationCheckYourEmail = lazy(() => import('./pages/Registration/ResendInvitationCheckYourEmail'));

// SIGNIN/PASSWORD
const RequestPasswordReset = lazy(() => import('./pages/SignIn/RequestPasswordReset'));
const RequestPasswordResetConfirmation = lazy(() => import('./pages/SignIn/RequestPasswordResetConfirmation'));
const ResetPassword = lazy(() => import('./pages/SignIn/ResetPassword'));
const SignIn = lazy(() => import('./pages/SignIn/SignIn'));

// DECLARATIONS
const Declaration = lazy(() => import('./pages/Declaration/Declaration'));
const DeclarationDashboard = lazy(() => import('./pages/Dashboard/DeclarationDashboard'));

// TEAM
const ManageUsers = lazy(() => import('./pages/Team/ManageUsers'));

// FOOTER
const AccessibilityStatement = lazy(() => import('./pages/Help/AccessibilityStatement'));
const Help = lazy(() => import('./pages/Help/Help'));
const PrivacyPolicy = lazy(() => import('./pages/Help/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/Help/CookiePolicy'));

const AppRouter = () => {
  document.title = SERVICE_NAME;
  const isAuthenticated = !!Auth.isAuthorized();

  return (
    <ScrollToTopOnNewPage>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path={LANDING_URL} element={<Landing />} />
          <Route path={SIGN_IN_URL} element={<SignIn />} />
          <Route path={ACTIVATE_ACCOUNT} element={<RegisterYourDetails />} />
          <Route path={REGISTER_DETAILS_URL} element={<RegisterYourDetails />} />
          <Route path={REGISTER_PASSWORD_URL} element={<RegisterYourPassword />} />
          <Route path={RESEND_INVITATION_URL} element={<ResendInvitation />} />
          <Route path={RESEND_INVITATION_CHECK_YOUR_EMAIL_URL} element={<ResendInvitationCheckYourEmail />} />
          <Route path={RESET_YOUR_PASSWORD} element={<ResetPassword />} />
          <Route path={REQUEST_PASSWORD_RESET_CONFIRMATION_URL} element={<RequestPasswordResetConfirmation />} />
          <Route path={REQUEST_PASSWORD_RESET_URL} element={<RequestPasswordReset />} />
          <Route path={MESSAGE_URL} element={<GenericMessage />} />
          <Route path={ACCESSIBILITY_URL} element={<AccessibilityStatement />} />
          <Route path={HELP_URL} element={<Help />} />
          <Route path={PRIVACY_URL} element={<PrivacyPolicy />} />
          <Route path={COOKIES_URL} element={<CookiePolicy />} />
          <Route path={SPCR_DASHBOARD_URL} element={<SpcrDashboard />} />

          <Route path={`${SPCR_REVIEW_REPORT_URL}/:spcrReportId`} element={<ReviewReport />} />

          <Route element={<ProtectedRoutes />}>
            <Route path={SELECT_SERVICE_URL} element={<SelectService />} />
            <Route path={`${DECLARATION_URL}/:declarationId`} element={<Declaration />} />
            <Route path={REPORTED_VOYAGES_DASHBOARD_URL} element={<DeclarationDashboard />} />
          </Route>
          <Route element={<AdminRoutes />}>
            <Route path={INVITE_USER_URL} element={<InviteUser />} />
            <Route path={INVITE_USER_CHECK_ANSWERS_URL} element={<InviteCheckYourAnswers />} />
            <Route path={INVITE_USER_CONFIRMATION_URL} element={<InviteConfirmation />} />
            <Route path={MANAGE_USERS_URL} element={<ManageUsers />} />
          </Route>
          <Route path="*" element={<Navigate to={isAuthenticated ? SELECT_SERVICE_URL : SIGN_IN_URL} replace />} />
        </Routes>
      </Suspense>
    </ScrollToTopOnNewPage>
  );
};

export default AppRouter;
